/**
 * "These two are cousins" — recording a stated relationship.
 *
 * Families state conclusions. They say *"Rachid and Nadia are first cousins"*,
 * and often that is genuinely all anyone knows: not the grandparent's name, not
 * always even the parents'. The tree stores only premises (ADR-0003 — kinship is
 * derived, never stored), so the honest translation is to record the people and
 * the parent edges the claim implies, and let the engine derive the label back
 * out of them.
 *
 * Two rules keep that from becoming invention:
 *
 *  - **Nothing is created without being shown first.** The plan is rendered as a
 *    list of "we will add …" before anything is written, because this is the one
 *    place in the application where a single click creates several people.
 *  - **An ambiguous line is a question, not a guess.** Somebody with a recorded
 *    mother *and* father has two possible cousinhoods; the family knows which,
 *    and the form asks rather than picking.
 */

import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  KIN_CLAIMS,
  KIN_CLAIM_NAMES,
  planKinshipSkeleton,
  type KinClaim,
  type PersonSummary,
  type SkeletonRef,
} from '@famtree/shared';
import { personsApi, relationsApi } from '../../api/client.js';
import { PersonPicker } from '../people/PersonPicker.js';
import { personName } from '../people/labels.js';
import { problemMessage } from '../../ui/problem.js';
import ui from '../../ui/components.module.css';
import styles from './kinship.module.css';

export interface RecordRelationshipProps {
  from: PersonSummary;
  to: PersonSummary;
  /** The parents each endpoint already has, so the plan reuses them. */
  fromParents: PersonSummary[];
  toParents: PersonSummary[];
  onRecorded: () => void;
}

export function RecordRelationship({
  from,
  to,
  fromParents,
  toParents,
  onRecorded,
}: RecordRelationshipProps): JSX.Element {
  const { t } = useTranslation();
  const [claim, setClaim] = useState<KinClaim>('first_cousin');
  const [chosen, setChosen] = useState<Record<string, string | null>>({});
  const [picking, setPicking] = useState<Record<string, PersonSummary | null>>({});
  const [created, setCreated] = useState<string[] | null>(null);

  const plan = useMemo(
    () =>
      planKinshipSkeleton({
        claim,
        fromParents: fromParents.map((person) => person.id),
        toParents: toParents.map((person) => person.id),
        chosen,
      }),
    [claim, chosen, fromParents, toParents],
  );

  const known = useMemo(() => {
    const map = new Map<string, PersonSummary>();
    for (const person of [from, to, ...fromParents, ...toParents]) map.set(person.id, person);
    for (const person of Object.values(picking)) if (person) map.set(person.id, person);
    return map;
  }, [from, to, fromParents, toParents, picking]);

  const toCreate = plan.slots.filter((slot) => slot.personId === null);

  const record = useMutation({
    mutationFn: async (): Promise<string[]> => {
      // People first: every one of them stands alone, so nothing here depends on
      // an id that does not exist yet.
      const ids = new Map<string, string>();
      for (const slot of plan.slots) {
        const slotKey = `${slot.side}:${String(slot.generation)}`;
        if (slot.personId) {
          ids.set(slotKey, slot.personId);
          continue;
        }
        const result = await personsApi.create({
          person: {
            given_names: null,
            family_name: null,
            birth_family_name: null,
            nickname: null,
            // Everything about this person is unknown except their position —
            // which is exactly what the tree is recording.
            gender: 'unknown',
            living_status: 'unknown',
          },
          source_note: t('kinship.claimSource', {
            claim: t(`kinship.claims.${claim}`),
            from: personName(from),
            to: personName(to),
          }),
        });
        ids.set(slotKey, result.person.id);
      }

      const resolve = (ref: SkeletonRef): string => {
        if (ref.kind === 'person') return ref.id;
        if (ref.kind === 'endpoint') return ref.side === 'from' ? from.id : to.id;
        const resolved = ids.get(`${ref.side}:${String(ref.generation)}`);
        if (!resolved) throw new Error('a rung of the plan was not created');
        return resolved;
      };

      for (const link of plan.links) {
        // The tree already holds this one — it is why the rung was reusable.
        if (link.existing) continue;
        await relationsApi.create({
          relation: {
            kind: 'parent_child',
            person_a_id: resolve(link.parent),
            person_b_id: resolve(link.child),
            parent_role: 'parent',
            // The claim says these people are related; it says nothing about how
            // the parenthood came about, and `unknown` is the honest basis.
            parent_basis: 'unknown',
          },
        });
      }

      return [...ids.values()];
    },
    onSuccess: (ids) => {
      setCreated(ids);
      onRecorded();
    },
  });

  if (created) {
    return (
      <div className={ui.notice} role="status">
        {t('kinship.claimRecorded', { count: toCreate.length })}
      </div>
    );
  }

  return (
    <section className={ui.card}>
      <h2>{t('kinship.recordTitle')}</h2>
      <p className={ui.muted}>{t('kinship.recordHelp')}</p>

      <label className={ui.field}>
        <span className={ui.label}>{t('kinship.recordClaim', { name: personName(to) })}</span>
        <select
          className={ui.input}
          value={claim}
          onChange={(event) => {
            setClaim(event.target.value as KinClaim);
            setChosen({});
            setPicking({});
          }}
        >
          {KIN_CLAIM_NAMES.map((name) => (
            <option key={name} value={name}>
              {t(`kinship.claims.${name}`)}
            </option>
          ))}
        </select>
      </label>

      {/* An ambiguous rung: which of their two recorded parents is the line? */}
      {plan.ambiguous.map((slot) => {
        const slotKey = `${slot.side}:${String(slot.generation)}`;
        const options = slot.side === 'from' ? fromParents : toParents;
        return (
          <fieldset key={slotKey} className={ui.field}>
            <legend className={ui.label}>
              {t('kinship.whichLine', {
                name: personName(slot.side === 'from' ? from : to),
              })}
            </legend>
            {options.map((option) => (
              <label key={option.id} style={{ display: 'block' }}>
                <input
                  type="radio"
                  name={slotKey}
                  checked={chosen[slotKey] === option.id}
                  onChange={() => setChosen((all) => ({ ...all, [slotKey]: option.id }))}
                />{' '}
                {personName(option)}
              </label>
            ))}
          </fieldset>
        );
      })}

      <h3 className={styles.groupTitle}>{t('kinship.planTitle')}</h3>
      <ul className={styles.list}>
        {plan.slots.map((slot) => {
          const slotKey = `${slot.side}:${String(slot.generation)}`;
          const existing = slot.personId ? known.get(slot.personId) : null;
          return (
            <li key={slotKey}>
              <span className={styles.label}>{t(`kinship.rung.${slot.side}`)}</span>{' '}
              {existing ? (
                <strong>{personName(existing)}</strong>
              ) : slot.personId ? (
                <strong>{t('kinship.alreadyRecorded')}</strong>
              ) : (
                <>
                  <em>{t('kinship.willCreate')}</em>{' '}
                  {/* Escape hatch: if the family *can* name this rung, or it is
                      already in the tree under a name, pick them instead. */}
                  <PersonPicker
                    label={t('kinship.orChoose')}
                    value={picking[slotKey] ?? null}
                    onChange={(person) => {
                      setPicking((all) => ({ ...all, [slotKey]: person }));
                      setChosen((all) => ({ ...all, [slotKey]: person?.id ?? null }));
                    }}
                  />
                </>
              )}
            </li>
          );
        })}
      </ul>
      <p className={ui.muted}>
        {t('kinship.planSummary', {
          people: toCreate.length,
          links: plan.links.filter((link) => !link.existing).length,
        })}
      </p>

      <button
        type="button"
        className={ui.button}
        disabled={record.isPending || plan.ambiguous.length > 0}
        onClick={() => record.mutate()}
      >
        {t('kinship.recordSubmit')}
      </button>
      {plan.ambiguous.length > 0 && <p className={ui.muted}>{t('kinship.answerFirst')}</p>}
      {record.isError && <p className={ui.problem}>{problemMessage(record.error, t)}</p>}
    </section>
  );
}

/** The claim vocabulary, for callers that want to label a shape. */
export const CLAIM_SHAPES = KIN_CLAIMS;
